/**
 * Store reactivo de las bandas de audio.
 * Encapsula el AnalizadorAudio y expone bandas como store legible.
 *
 * Patrón:
 * - `iniciarAudio()` debe llamarse desde un gesto del usuario (gesture-required).
 * - Una vez inicializado, `bandas` emite valores en cada frame.
 * - `detenerAudio()` pausa y libera recursos.
 *
 * Ver docs/SPEC-IMPLEMENTACION.md §4.2.
 */

import { writable, type Readable, type Writable } from 'svelte/store';
import { AnalizadorAudio } from '@/lib/audio/AnalizadorAudio';
import { BANDAS_INICIALES, type BandasAudio } from '@/lib/audio/tipos';

const RUTA_CANCION = '/audio/ghibli.mp3';

let analizador: AnalizadorAudio | null = null;
const bandasStore: Writable<BandasAudio> = writable<BandasAudio>(BANDAS_INICIALES);
const tiempoStore: Writable<number> = writable<number>(0);

/** Store legible de bandas (vacío hasta `iniciarAudio()`). */
export const bandas: Readable<BandasAudio> = { subscribe: bandasStore.subscribe };

/** Store reactivo de tiempo actual de reproducción en segundos. */
export const tiempoReproduccion: Readable<number> = { subscribe: tiempoStore.subscribe };

/** Inicializa el analizador y comienza la reproducción.
 *  Debe llamarse desde un click del usuario. */
export async function iniciarAudio(): Promise<void> {
  if (analizador) return; // ya inicializado

  analizador = new AnalizadorAudio(RUTA_CANCION);

  try {
    await analizador.iniciar();
    await analizador.reproducir();

    // Suscribir a las bandas y actualizar tiempo
    analizador.suscribirse((nuevasBandas) => {
      bandasStore.set(nuevasBandas);
      if (analizador) {
        tiempoStore.set(analizador.obtenerTiempoActual());
      }
    });
  } catch (err) {
    console.warn('[audioStore] No se pudo iniciar audio:', err);
    analizador.destruir();
    analizador = null;
  }
}

/** Pausa la reproducción. */
export function pausarAudio(): void {
  analizador?.pausar();
}

/** Detiene y libera recursos. */
export function detenerAudio(): void {
  analizador?.destruir();
  analizador = null;
  bandasStore.set(BANDAS_INICIALES);
}
